import { AccountCircle, Menu as MenuIcon } from "@mui/icons-material";
import MessageIcon from "@mui/icons-material/Message";
import { AppBar, IconButton, Toolbar, Divider, Box, styled, MenuItem } from "@mui/material";
import { rem } from "polished";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { HeaderLogo, SkipLink } from "src/components";
import { ariaLabel, TICKETS_PAGE } from "src/constants";
import { AnalyticsCustomEventNameEnum } from "src/graphql/generated/types";
import { useDeviceWidth, useUuid } from "src/hooks";
import { CreateTicket } from "src/pages/_shared/CreateTicket";
import { logCustomEvent } from "src/utils";
import { isActivationKey } from "src/utils/activationKey";

import { HeaderMenu } from "../HeaderMenu";
import { contentWidth } from "../layout";

export interface MainLayoutHeaderProps {
  hasRestrictedAccess: boolean;
  hasSeveralAccounts: boolean;
  isSiteMapPage: boolean;
  toAuth(): void;
  toProfile(): void;
  onLogout(): void;
  openSidebar(): void;
}

const StyledToolbar = styled(Toolbar)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: ${rem(contentWidth)};
  margin: auto;
  @media (max-width: ${rem(contentWidth)}) {
    width: 100vw;
    margin: initial;
  }
`;

const StyledLogo = styled("div")`
  display: flex;
  &:hover {
    cursor: ${(props) => (props.onClick ? "pointer" : "initial")};
  }
`;

const StyledMenuItem = styled(MenuItem)`
  padding: ${(props) => props.theme.spacing(2)} ${(props) => props.theme.spacing(4)};
  border-radius: 0;
  color: ${(props) => props.theme.palette.primary.main};
  font-weight: 500;
  display: flex;
  justify-content: center;

  &:hover {
    background-color: ${(props) => props.theme.palette.secondary.dark};
    color: ${(props) => props.theme.palette.secondary.contrastText};
    outline: 2px solid ${(props) => props.theme.palette.secondary.dark};
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0 !important;
`;

const StyledMenuIcon = styled(MenuIcon)`
  &.MuiSvgIcon-root {
    fill: ${(props) => props.theme.palette.primary.contrastText};
    width: 2rem;
    height: 2rem;
  }
`;

export const MainLayoutHeader = ({
  hasRestrictedAccess,
  hasSeveralAccounts,
  isSiteMapPage,
  toAuth,
  toProfile,
  onLogout,
  openSidebar,
}: MainLayoutHeaderProps) => {
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [ticketsAnchor, setTicketsAnchor] = useState<null | HTMLElement>(null);
  const [createTicketOpen, setCreateTicketOpen] = useState(false);
  const navigate = useNavigate();
  const uuid = useUuid();

  const { isTabletMobile } = useDeviceWidth();

  const handleProfileMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (profileAnchor) {
      setProfileAnchor(null);
    } else {
      setProfileAnchor(event.currentTarget);
      setTicketsAnchor(null);
    }
  };

  const handleTicketMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (ticketsAnchor) {
      setTicketsAnchor(null);
    } else {
      setTicketsAnchor(event.currentTarget);
      setProfileAnchor(null);
    }
  };

  const handleClose = useCallback(() => {
    setProfileAnchor(null);
    setTicketsAnchor(null);
  }, []);

  const goToTickets = () => {
    navigate(TICKETS_PAGE);
    handleClose();
  };

  const handleProfile = useCallback(() => {
    toProfile();
    handleClose();
  }, []);

  const handleLogoKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (hasSeveralAccounts && isActivationKey(event)) {
      toAuth();
    }
  };

  return (
    <AppBar position="static" aria-label={ariaLabel.header} role="banner">
      <SkipLink />
      <StyledToolbar>
        {isTabletMobile && !isSiteMapPage && (
          <IconButton aria-label={ariaLabel.openMenuButtonLabel} onClick={openSidebar}>
            <StyledMenuIcon />
          </IconButton>
        )}
        <StyledLogo
          role="link"
          tabIndex={0}
          onClick={hasSeveralAccounts ? toAuth : undefined}
          onKeyDown={handleLogoKeyDown}
        >
          <HeaderLogo />
        </StyledLogo>

        <Box display="flex">
          <IconButton
            id="tickets-menu-button"
            aria-label={ariaLabel.myTickets}
            aria-controls={ticketsAnchor ? "tickets-menu" : undefined}
            aria-expanded={Boolean(ticketsAnchor)}
            onClick={handleTicketMenu}
          >
            <MessageIcon fontSize="large" sx={{ color: "primary.contrastText" }} />
          </IconButton>
          <IconButton
            id="profil-menu-button"
            aria-label={ariaLabel.clientSettings}
            aria-controls={profileAnchor ? "profil-menu" : undefined}
            aria-expanded={Boolean(profileAnchor)}
            onClick={handleProfileMenu}
            color="inherit"
          >
            <AccountCircle fontSize="large" />
          </IconButton>
        </Box>

        <HeaderMenu
          anchorEl={profileAnchor}
          open={Boolean(profileAnchor)}
          onClose={handleClose}
          slotProps={{
            paper: {
              id: "profil-menu-paper",
            },
            list: {
              "aria-labelledby": "profil-menu-button",
              role: "menu",
              id: "profil-menu",
            },
          }}
        >
          {!hasRestrictedAccess && (
            <StyledMenuItem onClick={handleProfile} role="menuitem">
              Mon profil
            </StyledMenuItem>
          )}
          {!hasRestrictedAccess && <StyledDivider />}
          <StyledMenuItem onClick={onLogout} role="menuitem" data-cy="profil-menu-logout-button">
            Déconnexion
          </StyledMenuItem>
        </HeaderMenu>

        <HeaderMenu
          anchorEl={ticketsAnchor}
          open={Boolean(ticketsAnchor)}
          onClose={handleClose}
          slotProps={{
            paper: {
              id: "tickets-menu-paper",
            },
            list: {
              "aria-labelledby": "tickets-menu-button",
              role: "menu",
              id: "tickets-menu",
            },
          }}
        >
          {!hasRestrictedAccess && (
            <StyledMenuItem
              role="menuitem"
              id="create-ticket-menu-item"
              onClick={() => {
                handleClose();
                setCreateTicketOpen(true);
                logCustomEvent(AnalyticsCustomEventNameEnum.HeaderNewTicket);
              }}
            >
              Faire une demande
            </StyledMenuItem>
          )}
          <StyledMenuItem onClick={goToTickets} role="menuitem">
            Mes demandes
          </StyledMenuItem>
        </HeaderMenu>
      </StyledToolbar>
      <CreateTicket
        uuid={uuid}
        open={createTicketOpen}
        onClose={() => setCreateTicketOpen(false)}
      />
    </AppBar>
  );
};
